import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GestorLayout from '../../components/GestorLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2, AlertTriangle, RefreshCw, Server, Database,
  Clock, Activity, Shield, Cpu, GitBranch,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SistemaStatus({ embedded = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sistema/status`);
      setStatus(res.data);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Erro ao carregar status:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Auto-refresh a cada 30s
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (ts) =>
    ts ? new Date(ts).toLocaleString('pt-PT') : '—';

  const content = (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-navy flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" />
              Status do Sistema
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              {lastUpdate ? `Última actualização: ${lastUpdate.toLocaleTimeString('pt-PT')}` : 'A carregar...'}
            </p>
          </div>
          <Button variant="outline" onClick={loadStatus} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {loading && !status ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-8 w-8 text-stone-400 animate-spin" />
          </div>
        ) : status && (
          <>
            {/* Cards de resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  label: 'Servidor',
                  value: status.servidor === 'OK' ? 'Online' : 'Offline',
                  icon: Server,
                  color: status.servidor === 'OK' ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50',
                  ok: status.servidor === 'OK',
                },
                {
                  label: 'Base de Dados',
                  value: status.base_dados === 'conectado' ? 'Conectada' : 'Desconectada',
                  icon: Database,
                  color: status.base_dados === 'conectado' ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50',
                  ok: status.base_dados === 'conectado',
                },
                {
                  label: 'Routers',
                  value: `${status.routers_activos} activos`,
                  sub: `${status.total_endpoints} endpoints`,
                  icon: GitBranch,
                  color: 'text-blue-600 bg-blue-50',
                  ok: true,
                },
                {
                  label: 'Sessões Activas',
                  value: `${status.sessoes_activas}`,
                  sub: status.sessoes_activas > 0 ? 'em produção' : 'nenhuma em curso',
                  icon: Activity,
                  color: status.sessoes_activas > 0 ? 'text-amber-600 bg-amber-50' : 'text-stone-600 bg-stone-50',
                  ok: true,
                },
              ].map(({ label, value, sub, icon: Icon, color, ok }) => (
                <Card key={label} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">{label}</p>
                    {ok ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="font-bold text-navy">{value}</p>
                  {sub && <p className="text-xs text-stone-400">{sub}</p>}
                </Card>
              ))}
            </div>

            {/* Scheduler de Timeout */}
            <Card className="p-4 border-amber-200 bg-amber-50/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-stone-800">Scheduler de Timeout de Sessões</p>
                  <p className="text-sm text-stone-500">
                    {status.scheduler_status === 'a_correr' ? '✓ A correr' : '✗ Parado'} ·
                    Fecha sessões inactivas há mais de <strong>{status.scheduler_timeout_horas}h</strong> ·
                    Verifica de hora em hora
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  status.scheduler_status === 'a_correr'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {status.scheduler_status === 'a_correr' ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </Card>

            {/* Latências */}
            {status.latencias_ms && (
              <Card className="p-4">
                <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  Latências da Base de Dados
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(status.latencias_ms).map(([key, ms]) => {
                    const labels = { db_ping: 'Ping BD', ofs_count: 'OFs Query', operadores_count: 'Operadores Query' };
                    const color = ms < 5 ? 'text-emerald-600' : ms < 20 ? 'text-amber-600' : ms < 0 ? 'text-red-500' : 'text-orange-600';
                    return (
                      <div key={key} className="text-center p-3 bg-stone-50 rounded-lg">
                        <p className={`text-2xl font-bold font-mono ${color}`}>
                          {ms < 0 ? 'ERR' : `${ms}ms`}
                        </p>
                        <p className="text-xs text-stone-500 mt-1">{labels[key] || key}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-stone-400 mt-2 text-center">
                  Verde &lt;5ms · Amarelo &lt;20ms · Laranja ≥20ms
                </p>
              </Card>
            )}

            {/* Informação do sistema */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Cpu className="h-4 w-4 text-stone-500" />
                  <span className="text-xs text-stone-500 uppercase tracking-wide">Versão</span>
                </div>
                <p className="font-mono font-bold text-navy">{status.versao}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-stone-500" />
                  <span className="text-xs text-stone-500 uppercase tracking-wide">Timestamp</span>
                </div>
                <p className="text-sm text-stone-700">{formatTimestamp(status.timestamp)}</p>
              </Card>
            </div>

            {/* Lista de Routers */}
            <div>
              <h2 className="text-lg font-heading text-navy mb-3 flex items-center gap-2">
                <GitBranch className="h-5 w-5 text-primary" />
                Routers Activos ({status.routers_activos})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(status.routers || []).map((router) => (
                  <Card
                    key={router.nome}
                    className={`p-3 ${router.status !== 'activo' ? 'border-red-200 bg-red-50/30' : ''}`}
                    data-testid={`router-${router.nome}`}
                  >
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="font-medium text-stone-800 text-sm truncate">{router.nome}</p>
                      {router.status === 'activo' ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className={`text-xs ${router.status === 'activo' ? 'text-stone-400' : 'text-red-500'}`}>
                      {router.status === 'activo' ? `${router.endpoints} endpoint${router.endpoints !== 1 ? 's' : ''}` : router.status}
                    </p>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
  );

  return embedded ? content : <GestorLayout>{content}</GestorLayout>;
}